/* ═══════════════════════════════════════════════════════════════
   WidgetGrid — free-form dashboard grid with snap-to-cell placement.

   Built on react-grid-layout (battle-tested lib over hand-rolls,
   DESIGN.md §1): in customize mode every widget drags anywhere on a
   12-column × 56px-row cell grid. While dragging, a subtle accent
   ghost marks exactly which cells the widget will snap into (above /
   below / beside its neighbors — token-dock pattern), colliding
   widgets are pushed aside live, and vertical compaction keeps the
   board tight. Positions persist per widget as grid coordinates
   (x, y) in the layout entry, alongside the size tier.

   Sizes stay semantic: each widget size maps to a fixed cols × rows
   cell footprint (per-widget overrides via WidgetDef.rows). Content
   taller than its cell scrolls inside the card.

   Customize mode: drag-to-place, size segmented control, hide,
   add-from-catalog drawer, reset-to-default.
   ═══════════════════════════════════════════════════════════════ */

import React, { useRef } from 'react';
import GridLayout, { useContainerWidth, type Layout, type LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import './WidgetGrid.css';
import { hasPermission } from '../../shared/permissions';

export type WidgetSize = 'compact' | 'standard' | 'expanded';

export interface WidgetDef<Ctx = any> {
  id: string;
  title: string;
  requires?: string[];
  description?: string;
  defaultSize?: WidgetSize;
  /** Render the widget body. The size argument tells you which tier to
   *  render — bodies must produce distinct content per size. */
  render: (ctx: Ctx, size: WidgetSize) => React.ReactNode;
  /** Optional category so the catalog drawer groups widgets sensibly. */
  category?: 'overview' | 'financial' | 'people' | 'schedule' | 'health';
  /** Sizes this widget supports. A size is listed ONLY if it renders
   *  genuinely distinct, useful content — sizes that would just stretch
   *  the same body are omitted and never offered in the size picker.
   *  Undefined ⇒ all three. Saved layouts with an unsupported size are
   *  coerced to the nearest supported one. */
  sizes?: WidgetSize[];
  /** Fixed height in grid rows per size (ROW_UNIT cells). Overrides the
   *  defaults so a widget's cell matches its real content height. */
  rows?: Partial<Record<WidgetSize, number>>;
}

export interface WidgetLayoutEntry {
  id: string;
  size: WidgetSize;
  hidden?: boolean;
  /** Grid cell position (12-col × ROW_UNIT rows). Unset ⇒ auto-placed
   *  first-fit, then persisted after the user's first drag. */
  x?: number;
  y?: number;
  // Legacy compat — older layouts used cols/rows; ignored, kept so the
  // JSON parses without throwing.
  cols?: number;
  rows?: number;
}

export interface WidgetGridProps<Ctx> {
  widgets: WidgetDef<Ctx>[];
  layout: WidgetLayoutEntry[];
  userPermissions?: string[];
  ctx: Ctx;
  onLayoutChange?: (next: WidgetLayoutEntry[]) => void;
  defaultLayout?: WidgetLayoutEntry[];
  customizing: boolean;
  /** Optional slot rendered above the grid — used for the Saved Views bar. */
  viewsBar?: React.ReactNode;
}

const SIZE_ORDER: WidgetSize[] = ['compact', 'standard', 'expanded'];
const GRID_COLS = 12;
const SIZE_COLS: Record<WidgetSize, number> = { compact: 3, standard: 6, expanded: 12 };
export const ROW_UNIT = 56;
const DEFAULT_ROWS: Record<WidgetSize, number> = { compact: 2, standard: 4, expanded: 8 };
const SIZE_LABEL: Record<WidgetSize, string> = {
  compact: 'Compact',
  standard: 'Standard',
  expanded: 'Expanded',
};
const SIZE_GLYPH: Record<WidgetSize, string> = {
  compact: '□',
  standard: '▭',
  expanded: '▬',
};

const CATEGORY_LABEL: Record<NonNullable<WidgetDef['category']>, string> = {
  overview: 'Overview',
  financial: 'Financial',
  people: 'People',
  schedule: 'Schedule',
  health: 'Health',
};

export function WidgetGrid<Ctx>({
  widgets,
  layout,
  userPermissions,
  ctx,
  onLayoutChange,
  defaultLayout,
  customizing,
  viewsBar,
}: WidgetGridProps<Ctx>) {
  const [showCatalog, setShowCatalog] = React.useState(false);
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1280 });
  // Persist positions only after the user actually drags — react-grid-layout
  // also fires onLayoutChange for mount-time compaction, which must not
  // overwrite the stored layout.
  const userDragged = useRef(false);

  const allowedWidgets = widgets.filter(
    (w) => !w.requires || w.requires.every((k) => hasPermission(userPermissions, k))
  );
  const allowedIds = new Set(allowedWidgets.map((w) => w.id));

  // Migrate legacy layouts: if entry has cols/rows but no size, infer size
  const migrateEntry = (e: WidgetLayoutEntry): WidgetLayoutEntry => {
    if (e.size && SIZE_ORDER.includes(e.size)) return e;
    const cols = e.cols ?? 2;
    const size: WidgetSize = cols >= 4 ? 'expanded' : cols >= 2 ? 'standard' : 'compact';
    return { ...e, size };
  };

  // Coerce a saved size to one the widget actually supports (nearest by
  // rank) so trimming a widget's size list never breaks stored layouts.
  const supportedSizes = (id: string): WidgetSize[] => {
    const w = widgets.find((x) => x.id === id);
    return w?.sizes && w.sizes.length > 0 ? w.sizes : SIZE_ORDER;
  };
  const coerceSize = (e: WidgetLayoutEntry): WidgetLayoutEntry => {
    const supported = supportedSizes(e.id);
    if (supported.includes(e.size)) return e;
    const rank = SIZE_ORDER.indexOf(e.size);
    const nearest = supported.reduce((best, s) =>
      Math.abs(SIZE_ORDER.indexOf(s) - rank) < Math.abs(SIZE_ORDER.indexOf(best) - rank) ? s : best
    );
    return { ...e, size: nearest };
  };

  const rowsFor = (e: WidgetLayoutEntry): number => {
    const w = widgets.find((x) => x.id === e.id);
    return w?.rows?.[e.size] ?? DEFAULT_ROWS[e.size];
  };

  const effectiveLayout: WidgetLayoutEntry[] = (
    layout && layout.length > 0 ? layout : defaultLayout || []
  )
    .filter((e) => allowedIds.has(e.id))
    .map(migrateEntry)
    .map(coerceSize);

  const visibleEntries = effectiveLayout.filter((e) => !e.hidden);

  // Cell footprints + first-fit auto-placement for entries that have no
  // stored position yet (fresh installs, newly added widgets, defaults).
  const gridItems: Layout = (() => {
    type Cell = { x: number; y: number; w: number; h: number };
    const placed: Cell[] = [];
    const overlaps = (a: Cell, b: Cell) =>
      a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
    const out: LayoutItem[] = [];
    for (const e of visibleEntries) {
      const w = SIZE_COLS[e.size];
      const h = rowsFor(e);
      let cell: Cell | null =
        e.x != null && e.y != null ? { x: Math.min(e.x, GRID_COLS - w), y: e.y, w, h } : null;
      if (!cell) {
        for (let y = 0; cell === null && y < 500; y++) {
          for (let x = 0; x <= GRID_COLS - w; x++) {
            const cand = { x, y, w, h };
            if (!placed.some((p) => overlaps(cand, p))) {
              cell = cand;
              break;
            }
          }
        }
        if (!cell) cell = { x: 0, y: 500, w, h };
      }
      placed.push(cell);
      out.push({ i: e.id, x: cell.x, y: cell.y, w: cell.w, h: cell.h });
    }
    return out;
  })();

  const catalogWidgets = allowedWidgets.filter((w) => {
    const entry = effectiveLayout.find((e) => e.id === w.id);
    return !entry || entry.hidden;
  });

  function updateLayout(updater: (prev: WidgetLayoutEntry[]) => WidgetLayoutEntry[]) {
    onLayoutChange?.(updater(effectiveLayout));
  }

  /** Write RGL's computed cell positions back into the layout entries. */
  function persistPositions(l: Layout) {
    updateLayout((prev) =>
      prev.map((e) => {
        const item = l.find((it) => it.i === e.id);
        return item ? { ...e, x: item.x, y: item.y } : e;
      })
    );
  }

  function setWidgetSize(id: string, size: WidgetSize) {
    updateLayout((prev) => prev.map((e) => (e.id === id ? { ...e, size } : e)));
  }

  function hideWidget(id: string) {
    updateLayout((prev) => prev.map((e) => (e.id === id ? { ...e, hidden: true } : e)));
  }

  function showWidget(widget: WidgetDef<Ctx>) {
    updateLayout((prev) => {
      const existing = prev.find((e) => e.id === widget.id);
      if (existing)
        return prev.map((e) =>
          e.id === widget.id ? { ...e, hidden: false, x: undefined, y: undefined } : e
        );
      return [...prev, { id: widget.id, size: widget.defaultSize ?? 'standard', hidden: false }];
    });
    setShowCatalog(false);
  }

  // Group catalog by category
  const catalogByCategory = catalogWidgets.reduce<Record<string, WidgetDef<Ctx>[]>>((acc, w) => {
    const cat = w.category || 'overview';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(w);
    return acc;
  }, {});

  return (
    <div style={{ position: 'relative' }} ref={containerRef as React.RefObject<HTMLDivElement>}>
      {viewsBar}
      {mounted && (
        <GridLayout
          width={width}
          layout={gridItems}
          gridConfig={{
            cols: GRID_COLS,
            rowHeight: ROW_UNIT,
            margin: [16, 16],
            containerPadding: [0, 0],
          }}
          dragConfig={{ enabled: customizing, handle: '.wg-drag-handle' }}
          resizeConfig={{ enabled: false } as any}
          onDragStart={() => {
            userDragged.current = true;
          }}
          onLayoutChange={(l: Layout) => {
            if (!userDragged.current) return;
            userDragged.current = false;
            persistPositions(l);
          }}
        >
          {visibleEntries.map((entry) => {
            const widget = widgets.find((w) => w.id === entry.id);
            if (!widget) return <div key={entry.id} />;
            return (
              <div
                key={entry.id}
                data-widget-id={entry.id}
                style={{
                  background: 'var(--sf)',
                  border: '1px solid var(--bd)',
                  borderRadius: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  boxShadow: customizing ? '0 0 0 1px rgba(var(--acr), 0.08)' : 'none',
                }}
              >
                {/* Header — borderless in view mode so the card reads as one
                    quiet surface; the divider + tint appear only while
                    customizing, where the header is a control strip. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: customizing ? '10px 16px' : '14px 16px 4px',
                    borderBottom: customizing ? '1px solid var(--bd)' : 'none',
                    background: customizing ? 'rgba(var(--acr), 0.03)' : 'transparent',
                    flexShrink: 0,
                  }}
                >
                  {customizing && (
                    <span
                      className="wg-drag-handle"
                      role="button"
                      aria-label={`Move ${widget.title}`}
                      title="Drag to place — the ghost shows where it will snap"
                      style={{
                        cursor: 'grab',
                        color: 'var(--tm)',
                        fontSize: 14,
                        userSelect: 'none',
                        lineHeight: 1,
                        padding: '2px 4px',
                      }}
                    >
                      &#x2630;
                    </span>
                  )}
                  <span
                    className={customizing ? 'wg-drag-handle' : undefined}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--tx)',
                      flex: 1,
                      cursor: customizing ? 'grab' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    {widget.title}
                  </span>
                  {customizing && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {/* Size segmented — only the sizes this widget supports */}
                      <div
                        style={{
                          display: 'flex',
                          background: 'var(--sa)',
                          borderRadius: 5,
                          padding: 1,
                          gap: 1,
                        }}
                      >
                        {supportedSizes(entry.id).map((s) => (
                          <button
                            key={s}
                            onClick={() => setWidgetSize(entry.id, s)}
                            title={SIZE_LABEL[s]}
                            aria-label={`${SIZE_LABEL[s]} size`}
                            aria-pressed={entry.size === s}
                            style={{
                              padding: '5px 10px',
                              borderRadius: 4,
                              border: 'none',
                              background: entry.size === s ? 'var(--sf)' : 'transparent',
                              color: entry.size === s ? 'var(--ac)' : 'var(--tm)',
                              fontSize: 10,
                              fontWeight: entry.size === s ? 700 : 500,
                              cursor: 'pointer',
                              boxShadow: entry.size === s ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                              letterSpacing: 0.2,
                            }}
                          >
                            {SIZE_GLYPH[s]}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => hideWidget(entry.id)}
                        title="Hide widget"
                        aria-label="Hide widget"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 3,
                          border: '1px solid var(--bd)',
                          background: 'transparent',
                          color: 'var(--tm)',
                          cursor: 'pointer',
                          fontSize: 11,
                          lineHeight: 1,
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.color = 'var(--dg)';
                          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--dg)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.color = 'var(--tm)';
                          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--bd)';
                        }}
                      >
                        &#10005;
                      </button>
                    </div>
                  )}
                </div>
                {/* Body — the card is a fixed cell; taller content scrolls
                    inside it rather than growing the card. */}
                <div
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '8px 16px 16px',
                    minHeight: 0,
                  }}
                >
                  {widget.render(ctx, entry.size)}
                </div>
              </div>
            );
          })}
        </GridLayout>
      )}

      {/* Customize footer */}
      {customizing && (
        <div
          style={{
            marginTop: 16,
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() => setShowCatalog(true)}
            disabled={catalogWidgets.length === 0}
            style={{
              padding: '8px 18px',
              border: '1px dashed var(--bd)',
              borderRadius: 6,
              background: catalogWidgets.length === 0 ? 'var(--sa)' : 'transparent',
              color: catalogWidgets.length === 0 ? 'var(--tm)' : 'var(--ac)',
              fontSize: 12,
              fontWeight: 600,
              cursor: catalogWidgets.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            + Add widget{catalogWidgets.length > 0 ? ` (${catalogWidgets.length})` : ''}
          </button>
          {defaultLayout && defaultLayout.length > 0 && (
            <button
              onClick={() => updateLayout(() => defaultLayout.slice())}
              style={{
                padding: '8px 14px',
                border: '1px solid var(--bd)',
                borderRadius: 6,
                background: 'transparent',
                color: 'var(--tm)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              Reset to default
            </button>
          )}
          <span
            style={{ fontSize: 11, color: 'var(--tm)', flexBasis: '100%', textAlign: 'center' }}
          >
            Drag a widget anywhere — the highlight shows the cells it will snap into.
          </span>
        </div>
      )}

      {/* Catalog drawer */}
      {showCatalog && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowCatalog(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.4)',
            zIndex: 900,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: 420,
              background: 'var(--sf)',
              height: '100%',
              overflow: 'auto',
              boxShadow: '-2px 0 20px rgba(0,0,0,.2)',
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--bd)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Add widget</div>
                <div style={{ fontSize: 11, color: 'var(--tm)' }}>
                  {catalogWidgets.length} available
                </div>
              </div>
              <button
                onClick={() => setShowCatalog(false)}
                aria-label="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--tm)',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >
                &#10005;
              </button>
            </div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 18 }}>
              {(['overview', 'financial', 'people', 'schedule', 'health'] as const).map((cat) => {
                const items = catalogByCategory[cat];
                if (!items || items.length === 0) return null;
                return (
                  <div key={cat} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--tm)',
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        padding: '0 4px',
                      }}
                    >
                      {CATEGORY_LABEL[cat]}
                    </div>
                    {items.map((w) => (
                      <button
                        key={w.id}
                        onClick={() => showWidget(w)}
                        style={{
                          textAlign: 'left',
                          padding: '12px 14px',
                          border: '1px solid var(--bd)',
                          borderRadius: 8,
                          background: 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                          {w.title}
                        </span>
                        {w.description && (
                          <span style={{ fontSize: 11, color: 'var(--tm)', lineHeight: 1.4 }}>
                            {w.description}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
              {catalogWidgets.length === 0 && (
                <div
                  style={{
                    padding: 20,
                    textAlign: 'center',
                    color: 'var(--tm)',
                    fontSize: 12,
                  }}
                >
                  All widgets already on your dashboard.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
